import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Components/Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { useAuth } from '../AuthContext';

function SoulLink() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [soulGames, setSoulGames] = useState([]);
  const [availableGames, setAvailableGames] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addError, setAddError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [editingGame, setEditingGame] = useState(null);

  const fetchSoulGames = async () => {
    const [sgRes, pgRes] = await Promise.all([
      fetch(`${SUPABASE_URL}/rest/v1/soul_link_games?order=created_at.desc&select=*`, {
        headers: supabaseHeaders(false),
      }),
      fetch(`${SUPABASE_URL}/rest/v1/pokemon_games?select=*`, {
        headers: supabaseHeaders(false),
      }),
    ]);
    const sgData = await sgRes.json();
    const pgData = await pgRes.json();

    const pgMap = {};
    if (Array.isArray(pgData)) pgData.forEach(g => { pgMap[g.id] = g; });

    const merged = (Array.isArray(sgData) ? sgData : []).map(sg => ({
      ...sg,
      pokemon_games: pgMap[sg.pokemon_game_id] || null,
    }));

    setSoulGames(merged);
    setAvailableGames(Array.isArray(pgData) ? pgData : []);
  };

  useEffect(() => {
    fetchSoulGames();
  }, []);

  const openAdd = () => { setAddError(''); setShowAdd(true); };

  const handleAddGame = async (pokemonGame) => {
    setAddError('');
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/soul_link_games`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify({ pokemon_game_id: pokemonGame.id }),
      });
      const text = await res.text();
      if (!res.ok) {
        setAddError(`[${res.status}] ${text}`);
        return;
      }
      const data = text ? JSON.parse(text) : [];
      const newRecord = Array.isArray(data) ? data[0] : data;
      setSoulGames(prev => [{ ...newRecord, pokemon_games: pokemonGame }, ...prev]);
      setShowAdd(false);
    } catch (err) {
      setAddError(err.message);
    }
  };

  const deleteGame = async (id) => {
    setDeletingId(id);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/soul_link_games?id=eq.${id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      setEditingGame(null);
      setSoulGames(prev => prev.filter(g => g.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const addedIds = new Set(soulGames.map(g => g.pokemon_game_id));
  const pickable = availableGames.filter(g => !addedIds.has(g.id));

  const displayed = soulGames;

  return (
    <div className="app-container">
      <div className="header">
        <h1>Soul Link</h1>
        <div className="controls">
          <button onClick={() => navigate('/pokemon')} className="header-nav-btn">←</button>
          {user && <button onClick={openAdd}>Add +</button>}
        </div>
      </div>

      <div className="todo-grid">
        {displayed.map((sg, idx) => {
          const g = sg.pokemon_games;
          return (
            <div
              className="todo-card"
              key={sg.id}
              style={{ '--index': idx }}
              onClick={() => navigate(`/pokemon/soullink/${sg.id}`)}
              onDoubleClick={user ? e => { e.stopPropagation(); setEditingGame(sg); } : undefined}
            >
              {g?.poster_url && (
                <img src={g.poster_url} alt={g.name} className="todo-card-poster" />
              )}
              <div className="todo-card-title">{g?.name}</div>
            </div>
          );
        })}
      </div>

      {soulGames.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <h2 className="modal-title">Add Game</h2>
          <div className="modal-form">
            {addError && <p style={{ color: '#c0392b', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{addError}</p>}
            {pickable.length === 0 ? (
              <p style={{ color: '#888', fontSize: '0.9rem' }}>All games have been added already.</p>
            ) : (
              <div className="soul-link-game-picker">
                {pickable.map(g => (
                  <div
                    key={g.id}
                    className="soul-link-game-option"
                    onClick={() => handleAddGame(g)}
                  >
                    {g.poster_url && <img src={g.poster_url} alt={g.name} className="soul-link-game-option-img" />}
                    <span>{g.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="edit-modal-actions" style={{ marginTop: '1rem' }}>
              <button className="form-submit btn-cancel" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {editingGame && (
        <Modal onClose={() => setEditingGame(null)}>
          <h2 className="modal-title">{editingGame.pokemon_games?.name}</h2>
          <div className="modal-form">
            <div className="edit-modal-actions">
              <button
                className="form-submit btn-delete"
                disabled={deletingId === editingGame.id}
                onClick={() => deleteGame(editingGame.id)}
              >
                {deletingId === editingGame.id ? 'Deleting...' : 'Delete'}
              </button>
              <button className="form-submit btn-cancel" onClick={() => setEditingGame(null)}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default SoulLink;
